/**
 * @file     CostRail.tsx
 * @purpose  The listing cost rail: a flat panel at radius 20 with the monthly
 *           principal and interest, the down-payment slider, the rate field,
 *           the amortization control, a link to the full cost section and an
 *           opt-in broker introduction carrying the /disclosure referral-fee
 *           wording. Collapses to its figure on phones. Shares state with the
 *           cost section through ListingMortgageState.
 */
'use client';

import { useId, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { usePreApproval } from '@/components/lead-capture/PreApprovalContext';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { hairline, hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import { POSTED_FIVE_YEAR_RATE_PCT } from '@/core/finance/mortgage';
import { motionTransition } from '@/core/motion';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { MORTGAGE_DEFAULTS } from '@/features/mortgage-calculator-flow/types';
import { calculateMortgageBreakdown, estimateAnnualPropertyTax, formatCAD } from '@/utils/finance/mortgage';
import { BROKER_REFERRAL_DISCLOSURE } from '@/core/content/brokerReferralDisclosure';
import { useListingMortgageState } from './ListingMortgageState';
import { DETAIL_TAB_MORTGAGE, openDetailTab } from './DetailTabsShell';

export { BROKER_REFERRAL_DISCLOSURE };

interface Props {
  /** Pre-approval context key: the MLS listing key, or the curated slug. */
  listingKey: string;
  listingTitle: string;
  listPrice: number | null;
  taxAnnualAmount: number | null;
  associationFee: number | null;
  /** Pin below the header on desktop. */
  sticky?: boolean;
  /** Phone: collapse to the figure behind a disclosure button. */
  collapsible?: boolean;
  /** `summary` drops the controls and the broker block (curated listings). */
  variant?: 'full' | 'summary';
  /** Quick views have no cost section: See all costs opens this page instead. */
  allCostsHref?: string;
}

const AMORTIZATION_OPTIONS = [
  { value: '25', label: '25 yr' },
  { value: '30', label: '30 yr' },
];

function Chevron({ open }: { open: boolean }) {
  return (
    <Box
      component="svg"
      aria-hidden
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        color: hubColors.textSecondary,
        flexShrink: 0,
        transform: open ? 'rotate(180deg)' : 'none',
        transition: motionTransition('transform', 'base'),
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <path d="m6 9 6 6 6-6" />
    </Box>
  );
}

export function CostRail({
  listingKey,
  listingTitle,
  listPrice,
  taxAnnualAmount,
  associationFee,
  sticky = true,
  collapsible = false,
  variant = 'full',
  allCostsHref,
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const state = useListingMortgageState();
  const { open: openPreApproval } = usePreApproval();
  if (!state || listPrice == null) return null;

  const downPayment = Math.round(listPrice * (state.downPct / 100));
  const breakdown = calculateMortgageBreakdown({
    homePrice: listPrice,
    downPayment,
    annualInterestRate: state.rate,
    amortizationYears: state.amortization,
    annualPropertyTax: taxAnnualAmount ?? estimateAnnualPropertyTax(listPrice),
    annualInsurance: MORTGAGE_DEFAULTS.annualInsurance,
    monthlyHoa: associationFee ?? 0,
  });
  const monthly = formatCAD(breakdown.monthlyPrincipalAndInterest);
  const rateLine =
    state.rate === POSTED_FIVE_YEAR_RATE_PCT
      ? t('listingDetail.costRail.postedRate', { defaultValue: 'Posted 5-year rate {{rate}}%.', rate: state.rate })
      : t('listingDetail.costRail.yourRate', { defaultValue: 'Rate {{rate}}%.', rate: state.rate });
  const open = !collapsible || expanded;

  const figure = (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Box
        component="span"
        sx={{ fontSize: 32, fontWeight: fontWeightTokens.semibold, lineHeight: 1.1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: hubColors.textPrimary }}
      >
        {monthly}
      </Box>
      <Box component="span" sx={{ fontSize: 14, color: hubColors.textSecondary }}>
        {t('listingDetail.costRail.perMonth', { defaultValue: '/mo' })}
      </Box>
    </Box>
  );

  return (
    <Box
      component="aside"
      aria-label={t('listingDetail.costRail.label', { defaultValue: 'Monthly cost' })}
      data-cost-rail
      sx={{
        position: sticky ? 'sticky' : 'relative',
        top: sticky ? 'calc(var(--chrome-inset, 0px) + 88px)' : 'auto',
        p: 5,
        border: hairline,
        borderRadius: `${layoutTokens.radius.lg}px`,
        backgroundColor: hubColors.surfaceCard,
        color: hubColors.textPrimary,
      }}
    >
      {collapsible ? (
        <ButtonBase
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          sx={{
            width: '100%',
            minHeight: 44,
            justifyContent: 'space-between',
            gap: 3,
            textAlign: 'left',
            borderRadius: `${layoutTokens.radius.sm}px`,
            '&.Mui-focusVisible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 4 },
          }}
        >
          <Box>
            <Box component="p" sx={microLabelSx}>
              {t('listingDetail.costRail.label', { defaultValue: 'Monthly cost' })}
            </Box>
            {figure}
          </Box>
          <Chevron open={expanded} />
        </ButtonBase>
      ) : (
        <>
          <Box component="p" sx={microLabelSx}>
            {t('listingDetail.costRail.label', { defaultValue: 'Monthly cost' })}
          </Box>
          <Box sx={{ mt: 1 }}>{figure}</Box>
        </>
      )}

      <Box
        id={bodyId}
        hidden={!open}
        sx={{ mt: 2 }}
      >
        <Box component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.45, color: hubColors.textSecondary }}>
          {t('listingDetail.costRail.basis', { defaultValue: 'Principal and interest on {{price}}.', price: formatCAD(listPrice) })}{' '}
          {rateLine}
        </Box>

        {variant === 'full' ? (
          <>
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 14 }}>
                <Box component="span" sx={{ color: hubColors.textSecondary }}>
                  {t('listingDetail.costRail.downPayment', { defaultValue: 'Down payment' })}
                </Box>
                <Box component="span" sx={{ fontWeight: fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCAD(downPayment)} ({Math.round(state.downPct)}%)
                </Box>
              </Box>
              <Slider
                size="small"
                value={state.downPct}
                min={5}
                max={80}
                step={1}
                onChange={(_, value) => state.setDownPct(value as number)}
                aria-label={t('listingDetail.costRail.downPaymentLabel', { defaultValue: 'Down payment percentage' })}
                sx={{ color: hubColors.interactivePrimary, py: 2.5 }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 2, alignItems: 'center' }}>
              <TextField
                label={t('listingDetail.costRail.rate', { defaultValue: 'Rate (%)' })}
                size="small"
                type="number"
                value={state.rateInput}
                onChange={(event) => {
                  const raw = event.target.value;
                  state.setRateInput(raw);
                  const value = Number.parseFloat(raw);
                  if (value >= 0.5 && value <= 15) state.setRate(Math.round(value * 100) / 100);
                }}
                onBlur={() => state.setRateInput(String(state.rate))}
                slotProps={{ htmlInput: { min: 0.5, max: 15, step: 0.05, inputMode: 'decimal' } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${layoutTokens.radius.md}px`, minHeight: 44 } }}
              />
              <SegmentedControl
                size="sm"
                ariaLabel={t('listingDetail.costRail.amortization', { defaultValue: 'Amortization period' })}
                options={AMORTIZATION_OPTIONS}
                value={String(state.amortization)}
                onChange={(next) => state.setAmortization(next === '30' ? 30 : 25)}
              />
            </Box>
          </>
        ) : null}

        <Box
          {...(allCostsHref
            ? { component: 'a' as const, href: allCostsHref, target: '_blank', rel: 'noopener noreferrer' }
            : { component: 'button' as const, type: 'button' as const, onClick: () => openDetailTab(DETAIL_TAB_MORTGAGE, 'financial_breakdown') })}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
            mt: 3,
            p: 0,
            minHeight: 44,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 15,
            fontWeight: fontWeightTokens.medium,
            color: hubColors.link,
            '@media (hover: hover)': { '&:hover': { color: hubColors.linkHover, textDecoration: 'underline' } },
            '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2, borderRadius: '4px' },
          }}
        >
          {t('listingDetail.costRail.seeAllCosts', { defaultValue: 'See all costs' })}
        </Box>

        {variant === 'full' ? (
          <Box sx={{ mt: 3, pt: 4, borderTop: hairline }}>
            <Box component="h3" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold, lineHeight: 1.35 }}>
              {t('listingDetail.costRail.brokerTitle', { defaultValue: 'Talk to a mortgage broker' })}
            </Box>
            <Box component="p" sx={{ m: 0, mt: 1, fontSize: 13, lineHeight: 1.55, color: hubColors.textSecondary }}>
              {t('preApprovalFlow.brokerIntro.lead', { defaultValue: 'Learn how to get a pre-approval from independent mortgage brokers.' })}{' '}
              {BROKER_REFERRAL_DISCLOSURE}{' '}
              <Box component="a" href="/disclosure" sx={{ color: hubColors.link, textDecoration: 'underline' }}>
                {t('listingDetail.costRail.disclosureLink', { defaultValue: 'Referral disclosure' })}
              </Box>
            </Box>
            <Box sx={{ mt: 3 }}>
              <HubActionButton
                variant="secondary"
                fullWidth
                action={{
                  label: t('listingDetail.costRail.brokerCta', { defaultValue: 'Request an introduction' }),
                  onClick: () => openPreApproval({ listingKey, listingTitle, listingPrice: listPrice }),
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
